<template>
  <div class="table-responsive">
    <table class="table table-sm align-middle hourly-heatmap-table">
      <thead>
        <tr>
          <th class="sticky-col">Date</th>
          <th v-for="hour in hourHeaders" :key="`head-${hour}`" class="text-center">
            {{ hour.toString().padStart(2, "0") }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in rows" :key="row.date">
          <th class="sticky-col fw-semibold">{{ row.date }}</th>
          <td
            v-for="hour in hourHeaders"
            :key="`${row.date}-${hour}`"
            class="text-center heatmap-cell"
            :style="getCellStyle(row.byHour[hour])"
            :title="getCellTitle(row.byHour[hour], row.date, hour)"
          >
            {{ formatCellValue(row.byHour[hour]) }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
export default {
  name: "HourlyHeatmap",
  props: {
    heatmap: {
      type: Array,
      default: () => [],
    },
    metric: {
      type: String,
      default: "revenue",
      validator: (value) => ["revenue", "photos"].includes(value),
    },
  },
  computed: {
    hourHeaders() {
      return Array.from({ length: 24 }, (_, index) => index);
    },
    rows() {
      const map = new Map();

      for (const point of this.heatmap) {
        const date = point.date;
        const hour = Number(point.hour);

        if (!map.has(date)) {
          map.set(date, {
            date,
            byHour: {},
          });
        }

        map.get(date).byHour[hour] = point;
      }

      return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
    },
    maxValue() {
      const values = this.heatmap.map((item) => Number(item?.[this.metric] || 0));
      return Math.max(...values, 0);
    },
  },
  methods: {
    formatCellValue(cell) {
      const value = Number(cell?.[this.metric] || 0);
      if (value <= 0) {
        return "";
      }

      return this.metric === "revenue" ? value.toFixed(1) : Math.round(value);
    },
    getCellTitle(cell, date, hour) {
      const photos = Number(cell?.photos || 0);
      const revenue = Number(cell?.revenue || 0);
      const label = cell?.label || `${hour.toString().padStart(2, "0")}:00`;

      return `${date} ${label} | Photos: ${photos} | Revenue: ${revenue.toFixed(2)} GEL`;
    },
    getCellStyle(cell) {
      const value = Number(cell?.[this.metric] || 0);
      if (value <= 0 || this.maxValue === 0) {
        return {
          backgroundColor: "#f8f9fa",
          color: "#adb5bd",
        };
      }

      const intensity = value / this.maxValue;
      const alpha = 0.2 + intensity * 0.75;
      const [red, green, blue] =
        this.metric === "revenue" ? [25, 135, 84] : [13, 110, 253];

      return {
        backgroundColor: `rgba(${red}, ${green}, ${blue}, ${alpha})`,
        color: alpha > 0.6 ? "#ffffff" : "#212529",
      };
    },
  },
};
</script>

<style scoped>
.hourly-heatmap-table {
  min-width: 980px;
}

.hourly-heatmap-table th,
.hourly-heatmap-table td {
  font-size: 0.75rem;
}

.heatmap-cell {
  min-width: 42px;
  font-weight: 600;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: #ffffff;
  z-index: 1;
  min-width: 100px;
}

@media (max-width: 768px) {
  .hourly-heatmap-table th,
  .hourly-heatmap-table td {
    font-size: 0.65rem;
    padding: 0.2rem;
  }

  .heatmap-cell {
    min-width: 36px;
  }

  .sticky-col {
    min-width: 82px;
  }
}
</style>
